import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, useCan } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { StatusBadge } from '@/components/features/WorkOrders/components/StatusBadge';

interface InlineStatusCellProps {
    value: string;
    onUpdate?: (value: string) => void;
    className?: string;
    statuses?: Array<{ value: string; label: string; color: string; isTerminal?: boolean }>;
    readOnly?: boolean;
}

export function InlineStatusCell({ value, onUpdate, className, statuses, readOnly = false }: InlineStatusCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const { can } = useCan();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const canUpdateStatus = can('work-orders.update-status');
    const canCompleteWorkOrder = can('work-orders.complete-work-order');
    const canCancelWorkOrder = can('work-orders.can-cancel-work-order');

    const normalizeStatusValue = (status: string): string => status.trim().toLowerCase().replace(/\s+/g, '_');
    const isCompletedStatus = (status: string): boolean => normalizeStatusValue(status) === 'completed';
    const isCanceledStatus = (status: string): boolean => ['canceled', 'cancelled'].includes(normalizeStatusValue(status));

    const canSelectStatus = (status: string): boolean => {
        if (isCompletedStatus(status)) {
            return canCompleteWorkOrder;
        }

        if (isCanceledStatus(status)) {
            return canCancelWorkOrder;
        }

        return canUpdateStatus;
    };

    const handleSelect = async (newValue: string) => {
        if (!canSelectStatus(newValue)) {
            return;
        }

        if (newValue === value) {
            setIsOpen(false);
            return;
        }

        if (!onUpdate) return;

        setIsLoading(true);
        try {
            await onUpdate(newValue);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to update status:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const options = (statuses ?? []).map((status) => ({ value: status.value, label: status.label }));
    const hasCurrentValue = options.some((option) => option.value === value);
    if (!hasCurrentValue && value) {
        options.push({ value, label: value });
    }
    const hasOptions = options.length > 0;
    const hasSelectableOption = options.some((option) => canSelectStatus(option.value));

    // Read-only mode: just display the badge
    if (readOnly || !onUpdate || !hasOptions || !hasSelectableOption) {
        return (
            <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>
                <StatusBadge status={value} statuses={statuses} />
            </div>
        );
    }

    if (isMobile) {
        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(true);
                        }}
                        className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        ) : (
                            <StatusBadge status={value} statuses={statuses} />
                        )}
                    </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>{t('work_orders.index.columns.status', 'Status')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-2">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className={cn(
                                    'bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left',
                                    !canSelectStatus(option.value) && 'hover:bg-background cursor-not-allowed opacity-50',
                                )}
                                disabled={!canSelectStatus(option.value)}
                            >
                                <StatusBadge status={option.value} statuses={statuses} />
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    ) : (
                        <StatusBadge status={value} statuses={statuses} />
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                {options.map((option) => (
                    <DropdownMenuItem
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className={cn(!canSelectStatus(option.value) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
                        disabled={!canSelectStatus(option.value)}
                    >
                        <StatusBadge status={option.value} statuses={statuses} />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
